import { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';
import { Badge } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { setAlert } from '../../../actions/alert';
import { buscarInventario } from '../../../actions/inventario';
import { crearComanda, buscarEventos } from '../../../actions/comandas';
import { buscarServicios, buscarPaquetesPunto } from '../../../actions/punto';
import { filtarProductos } from '../../../actions/ordenes';

import { formatNumber, roundNumber } from '../../../utils/generalFunctions';

import FiltrosProducto from './FiltrosProducto';

import { CustomValueContainer } from '../../../components/react_select/ReactSelect';

const NuevaComanda = () => {

    let rutasBread = [
        {
            activo: false,
            nombre: 'Comandas',
            path: '/comandas'
        },
        {
            activo: true,
            nombre: 'Nueva comanda',
            path: '/comandas/nuevo'
        }
    ];

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const { listado_eventos } = useSelector(state => state.comandas);
    const { listado: listado_productos } = useSelector(state => state.inventario);
    const { listado_servicios, listado_paquetes } = useSelector(state => state.punto);

    const [comandaData, setComandaData] = useState({
        id_evento: null,
        monto_calculado: 0,
        busqueda_servicio: null,
        busqueda_paquete: null,
        productos: [],
        servicios: []
    });

    const [tipoComanda, setTipoComanda] = useState(0);

    const [eventoData, setEventoData] = useState(null);
    const [eventosFilter, setEventosFilter] = useState([]);

    const [productosSolicitar, setProductosSolicitar] = useState([]);
    const [productosTotales, setProductosTotales] = useState([]);

    const [unidadesData, setUnidadesData] = useState([]);

    const [buscadorServicios, setBuscadorServicios] = useState(false);

    const [servicioData, setServicioData] = useState(null);
    const [serviciosFilter, setServiciosFilter] = useState([]);

    const [buscadorPaquetes, setBuscadorPaquetes] = useState(false);

    const [paqueteData, setPaqueteData] = useState(null);
    const [paquetesFilter, setPaquetesFilter] = useState([]);

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const columnsAdd = [
        {
            name: '',
            grow: 0,
            cell: (row, index) => {
                return(
                    <div className="form-check" key={'producto-' + row.id}>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id={'producto-' + row.id}
                            onChange={e => handleCheckboxRemove(e, index)}
                        />
                    </div>
                )
            }
        }, 
        {
            name: 'Nombre',
            selector: row => row.nombre,
            grow: 4,
            wrap: true
        },
        {
            name: 'Precio',
            cell: (row, index) => formatNumber(row.precio_venta / (unidadesData[index]?.cantidad || 1), 2, '$ ', '')
        }, 
        {
            name: 'Cantidad actual',
            cell: (row, index) => {
                return(
                    <div className="w-100 text-end">
                        <h5><Badge bg={(row.cantidad * (unidadesData[index]?.cantidad || 1)) > row.reorden ? 'success' : ((row.cantidad * (unidadesData[index]?.cantidad || 1)) < row.reorden ? 'danger' : 'warning')}>{roundNumber(row.cantidad * (unidadesData[index]?.cantidad || 1), 2)}</Badge></h5>
                    </div>
                )
            }
        }, 
        {
            name: 'Cantidad a solicitar',
            cell: (row, index) => {
                return(
                    <input type="number" className="form-control" value={comandaData.productos[index]?.cantidad_solicitar} onChange={e => handleCantidadChange(e, index)}/>
                )
            }
        }, 
        {
            name: 'Unidad',
            grow: 1.5,
            cell: (row, index) => {
                return(
                    <div className="input-group h-100 py-2">
                        <Select
                            className="select-group"
                            classNamePrefix="react-select"
                            placeholder="Uso"
                            options={row.unidades}
                            value={unidadesData[index]}
                            isClearable={true}
                            onChange={(value) => handleSelectUnidad(value, index)}
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            components={{
                                ValueContainer: CustomValueContainer
                            }}
                            styles={{ ...styles_select }}
                        />
                    </div>
                )
            }
        }
    ];

    const columnsAll = [
        {
            name: '',
            grow: 0,
            cell: row => {
                return(
                    <div className="form-check" key={'producto-' + row.id}>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id={'producto-' + row.id}
                            onChange={e => handleCheckboxAdd(e)}
                        />
                    </div>
                )
            }
        }, 
        {
            name: 'Nombre',
            selector: row => row.nombre,
            grow: 2,
            wrap: true
        },
        {
            name: 'Categoría',
            selector: row => row.categoria.nombre || 'Sin definir'
        },
        {
            name: 'Subcategoría',
            selector: row => row.subcategoria.nombre || 'Sin definir'
        },
        {
            name: 'Subsubcategoría',
            selector: row => row.subsubcategoria.nombre || 'Sin definir'
        },
        {
            name: 'Cantidad actual',
            cell: row => {
                return(
                    <div className="w-100 text-end">
                        <h5><Badge bg={row.cantidad > row.reorden ? 'success' : (row.cantidad < row.reorden ? 'danger' : 'warning')}>{roundNumber(row.cantidad, 2)}</Badge></h5>
                    </div>
                )
            }
        }
    ];

    const columnsServicios = [
        {
            name: 'Servicio',
            cell: row => row.nombre,
            grow: 4
        },
        {
            name: 'Precio actual',
            cell: row => formatNumber(row.precio_actual, 2, '$ ', '')
        },
        {
            name: 'Precio final',
            cell: (row, index) => {
                return(
                    <CurrencyInput
                        className="form-control text-center"
                        value={comandaData.servicios[index]?.precio || ''}
                        decimalsLimit={2}
                        groupSeparator=","
                        decimalSeparator="."
                        onValueChange={(value) => handlePrecioChange(value, index)}
                    />
                )
            }
        }, 
        {
            name: 'Cantidad',
            cell: (row, index) => {
                return(
                    <input type="number" step="1" className="form-control" value={comandaData.servicios[index]?.cantidad} onChange={e => handleCantidadServicioChange(e, index)}/>
                )
            }
        },
        {
            name: '',
            cell: (row, index) => {
                return(
                    <div className="text-right w-100">
                        <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleRemoveServicio(index)}><i className="fas fa-trash text-danger"></i></button>
                    </div>
                )
            }
        }
    ];

    let styles_select = {
        menuPortal: base => ({ ...base, zIndex: 9999 }), 
        container: (provided, state) => ({
            ...provided
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            overflow: "visible", 
            marginTop: "15px"
        }),
        placeholder: (provided, state) => ({
            ...provided,
            position: "absolute",
            top: state.hasValue || state.selectProps.inputValue ? -15 : "0%",
            transition: "top 0.1s, font-size 0.1s",
            fontSize: (state.hasValue || state.selectProps.inputValue) && 13
        })
    };

    /* Nueva comanda */
    const handleSelectEvento = (option) => {
        setComandaData({
            ...comandaData,
            id_evento: option ? option.value : null
        });

        setEventoData(option);
    }

    const handleInputChange = ({ target }) => {
        setComandaData({
            ...comandaData,
            [target.name]: target.value
        });
    }

    /* Productos */
    const handleFiltrosBusqueda = () => {
        dispatch(filtarProductos(true));
    }

    const handleCheckboxAdd = ({ target }) => {
        const productoId = target.id.split('-')[1];

        let producto_solicitar = productosTotales.find(producto => producto.id == productoId);

        if (producto_solicitar) {
            let producto_existente = productosSolicitar.find(producto => producto.id == producto_solicitar.id);

            setProductosTotales(productosTotales.filter(producto => producto.id != productoId));

            if(!producto_existente){

                setProductosSolicitar([
                    ...productosSolicitar,
                    { ...producto_solicitar, precio_venta: 0 }
                ]);

                let productos = comandaData.productos;
                let unidades = unidadesData;

                productos.push({
                    id: productoId,
                    unidad: null,
                    conversion: 1,
                    cantidad_actual: producto_solicitar.cantidad,
                    cantidad_solicitar: 0
                });

                unidades.push(null);

                setComandaData({
                    ...comandaData, 
                    productos
                });

                setUnidadesData(unidades);
            }else{
                dispatch(setAlert('Ya tienes este producto en el listado a solicitar', 'danger'));
            }
        }
    }

    const handleCantidadChange = ({ target }, posicion) => {        
        let productos = comandaData.productos;
        let servicios = comandaData.servicios;
        let monto_calculado = 0;

        if(target.value < 0){
            dispatch(setAlert('No puedes ingresar cantidades negativas', 'danger'));
        }else if(target.value <= roundNumber(parseFloat(productos[posicion].cantidad_actual) * parseFloat(productos[posicion].conversion || 1), 2)){
            productos[posicion].cantidad_solicitar = target.value || '';
        }else{
            dispatch(setAlert('No puedes mover más cantidad de la existente', 'danger'));
        }

        for (var i = 0; i < productosSolicitar.length; i++) {
            monto_calculado += parseFloat(productos[i].cantidad_solicitar) * (productosSolicitar[i].precio_venta / (unidadesData[i]?.cantidad || 1));
        }

        for (var i = 0; i < servicios.length; i++) {
            monto_calculado += parseFloat(servicios[i].cantidad || 1) * parseFloat(servicios[i].precio || 0);
        }

        setComandaData({
            ...comandaData,
            productos, 
            monto_calculado
        });
    }

    const handleSelectUnidad = (option, posicion) => {
        let productos = comandaData.productos;
        let servicios = comandaData.servicios;
        let unidades = unidadesData;
        let monto_calculado = 0;

        let inventario_convertido = roundNumber(productos[posicion].cantidad_actual * (option ? option.cantidad : 1), 2);

        if(productos[posicion].cantidad_solicitar <= inventario_convertido){

            unidades[posicion] = option;

            productos[posicion].unidad = option ? option.value : null;
            productos[posicion].conversion = option ? option.cantidad : 1;

            setUnidadesData(unidades);

            for (var i = 0; i < productosSolicitar.length; i++) {
                monto_calculado += parseFloat(productos[i].cantidad_solicitar) * (productosSolicitar[i].precio_venta / (productos[i].conversion || 1));
            }

            for (var i = 0; i < servicios.length; i++) {
                monto_calculado += parseFloat(servicios[i].cantidad || 1) * parseFloat(servicios[i].precio || 0);
            }

            setComandaData({
                ...comandaData,
                productos,
                monto_calculado
            });
        }else{
            dispatch(setAlert('No puedes mover más cantidad de la existente', 'danger'));
        }
    }

    const handleCheckboxRemove = ({ target }, posicion) => {
        const productoId = target.id.split('-')[1];

        let producto_eliminar = productosSolicitar.find(producto => producto.id == productoId);

        if (producto_eliminar) {
            let producto_existente = productosTotales.find(producto => producto.id == producto_eliminar.id);
            let productos_solicitar = productosSolicitar.filter(producto => producto.id != productoId);

            setProductosSolicitar(productos_solicitar);

            if(!producto_existente){
                let productos_totales = [
                    ...productosTotales, 
                    producto_eliminar
                ];

                productos_totales = productos_totales.sort(function (a, b) {
                    if (a.nombre > b.nombre) {
                        return 1;
                    }
                    if (a.nombre < b.nombre) {
                        return -1;
                    }
                    return 0;
                });

                setProductosTotales(productos_totales);
            }else{
                dispatch(setAlert('Ya tienes este producto en el listado total', 'danger'));
            }

            let productos = comandaData.productos;
            let servicios = comandaData.servicios;
            let unidades = unidadesData;
            let monto_calculado = 0;

            productos.splice(posicion, 1);
            unidades.splice(posicion, 1);

            for (var i = 0; i < productos_solicitar.length; i++) {
                monto_calculado += parseFloat(productos[i].cantidad_solicitar) * (productos_solicitar[i].precio_venta / (unidades[i]?.cantidad || 1));
            }

            for (var i = 0; i < servicios.length; i++) {
                monto_calculado += parseFloat(servicios[i].cantidad || 1) * parseFloat(servicios[i].precio || 0);
            }

            setComandaData({
                ...comandaData, 
                productos, 
                monto_calculado
            });

            setUnidadesData(unidades);
        }
    }

    /* Servicios */
    const handleBuscarServicios = () => {
        if(!buscadorServicios){
            dispatch(buscarServicios({
                nombre: comandaData.busqueda_servicio
            }));
        }else{
            setComandaData({
                ...comandaData,
                busqueda_servicio: null
            });
        }

        setBuscadorServicios(!buscadorServicios);
    }

    const handleSelectServicio = (option) => {
        let productos = comandaData.productos;
        let servicios = comandaData.servicios;
        let monto_calculado = 0;

        if(option){
            let servicio_existente = servicios.some(servicio => 
                servicio.id_servicio == option.value
            );

            if(!servicio_existente){
                servicios.push({
                    id_servicio: option.value,
                    nombre: option.label,
                    precio_actual: option.precio,
                    precio: option.precio,
                    cantidad: 1
                });

                for (var i = 0; i < productos.length; i++) {
                    monto_calculado += parseFloat(productos[i].cantidad_solicitar) * (productosSolicitar[i].precio_venta / (productos[i].conversion || 1));
                }

                for (var i = 0; i < servicios.length; i++) {
                    monto_calculado += parseFloat(servicios[i].cantidad || 1) * parseFloat(servicios[i].precio || 0);
                }
            }else{
                dispatch(setAlert('Ya esta agregado este servicio a la comanda', 'danger'));
            }
        }

        setComandaData({
            ...comandaData,
            servicios, 
            monto_calculado
        });

        setServicioData(null);
    }

    const handleCantidadServicioChange = ({ target }, posicion) => {
        let productos = comandaData.productos;
        let servicios = comandaData.servicios;
        let monto_calculado = 0;

        if(target.value >= 0){
            servicios[posicion].cantidad = parseInt(target.value) || '';

            for (var i = 0; i < productosSolicitar.length; i++) {
                monto_calculado += parseFloat(productos[i].cantidad_solicitar) * (productosSolicitar[i].precio_venta / (unidadesData[i]?.cantidad || 1));
            }

            for (var i = 0; i < servicios.length; i++) {
                monto_calculado += parseFloat(servicios[i].cantidad || 1) * parseFloat(servicios[i].precio || 0);
            }

            setComandaData({ ...comandaData, servicios, monto_calculado });
        } else {
            dispatch(setAlert('No puedes ingresar cantidades negativas', 'danger'));
        }
    }

    const handlePrecioChange = (value, posicion) => {
        let productos = comandaData.productos;
        let servicios = comandaData.servicios;
        let monto_calculado = 0;
        
        if(value >= 0){
            servicios[posicion].precio = value || '';

            for (var i = 0; i < productos.length; i++) {
                monto_calculado += parseFloat(productos[i].cantidad_solicitar) * (productosSolicitar[i].precio_venta / (productos[i].conversion || 1));
            }

            for (var i = 0; i < servicios.length; i++) {
                monto_calculado += parseFloat(servicios[i].cantidad || 1) * parseFloat(servicios[i].precio || 0);
            }

            setComandaData({
                ...comandaData,
                servicios,
                monto_calculado
            });
        }else{
            dispatch(setAlert('No puedes vender montos negativos', 'danger'));
        }
    }

    const handleRemoveServicio = (posicion) => {
        let productos = comandaData.productos;
        let servicios = comandaData.servicios;
        let monto_calculado = 0;

        servicios.splice(posicion, 1);

        for (var i = 0; i < productos.length; i++) {
            monto_calculado += parseFloat(productos[i].cantidad_solicitar) * (productosSolicitar[i].precio_venta / (productos[i].conversion || 1));
        }

        for (var i = 0; i < servicios.length; i++) {
            monto_calculado += parseFloat(servicios[i].cantidad || 1) * parseFloat(servicios[i].precio || 0);
        }

        setComandaData({
            ...comandaData,
            servicios, 
            monto_calculado
        });
    }

    /* Paquetes */
    const handleBuscarPaquetes = () => {
        if (!buscadorPaquetes) {
            dispatch(buscarPaquetesPunto({
                nombre: comandaData.busqueda_paquete
            }));
        } else {
            setComandaData({
                ...comandaData,
                busqueda_paquete: null
            });
        }

        setBuscadorPaquetes(!buscadorPaquetes);
    }

    const handleSelectPaquete = (option) => {
        if (!option) return;

        let productos = [...comandaData.productos];
        let servicios = [...comandaData.servicios];
        let solicitar = [...productosSolicitar];
        let unidades = [...unidadesData];
        let monto_calculado = 0;

        for (const item of option.productos || []) {
            const idx = solicitar.findIndex(p => p.id == item.id);
            if (idx >= 0) {
                productos[idx].cantidad_solicitar = parseFloat(productos[idx].cantidad_solicitar || 0) + parseFloat(item.cantidad || 1);
            } else {
                solicitar.push({
                    id: item.id,
                    nombre: item.nombre || 'Sin definir',
                    precio_venta: 0,
                    cantidad: item.inventario || 0,
                    reorden: 0,
                    unidades: item.unidades || []
                });
                productos.push({
                    id: item.id,
                    unidad: item.id_unidad || null,
                    conversion: item.conversion || 1,
                    cantidad_actual: item.inventario || 0,
                    cantidad_solicitar: item.cantidad || 1
                });
                unidades.push(item.unidades?.find(u => u.value == item.id_unidad) || null);
            }
        }

        for (const item of option.servicios || []) {
            const idx = servicios.findIndex(s => s.id_servicio == item.id);
            if (idx >= 0) {
                servicios[idx].cantidad = parseInt(servicios[idx].cantidad || 1) + parseInt(item.cantidad || 1);
            } else {
                servicios.push({
                    id_servicio: item.id,
                    nombre: item.nombre || 'Sin definir',
                    precio_actual: item.precio_venta || 0,
                    precio: item.precio_venta || 0,
                    cantidad: item.cantidad || 1
                });
            }
        }

        for (var i = 0; i < solicitar.length; i++) {
            monto_calculado += parseFloat(productos[i].cantidad_solicitar || 0) * (solicitar[i].precio_venta / (unidades[i]?.cantidad || 1));
        }

        for (var i = 0; i < servicios.length; i++) {
            monto_calculado += parseFloat(servicios[i].cantidad || 1) * parseFloat(servicios[i].precio || 0);
        }

        setProductosSolicitar(solicitar);
        setUnidadesData(unidades);
        setComandaData({ ...comandaData, productos, servicios, monto_calculado });
        setPaqueteData(null);
    }

    const handleGuardarComanda = async () => {
        if(!comandaData.id_evento){
            await dispatch(setAlert('Debes de seleccionar un evento', 'danger'));
        }else if(comandaData.productos.length > 0 || comandaData.servicios.length > 0){
            await dispatch(crearComanda(comandaData));

            await navigate("/comandas");
        }else{
            await dispatch(setAlert('Debe de haber al menos un producto o servicio', 'danger'));
        }
    }

    useEffect(() => {
        dispatch(buscarEventos());
        dispatch(buscarInventario({
            nombre: null, 
            categoria: null, 
            subcategoria: null, 
            subsubcategoria: null, 
            codigo_barras: null, 
            status: 0, 
            tipo_unidades: 2
        }));
    }, []);

    useEffect(() => {
        let options = [];

        for (var i = 0; i < listado_eventos.length; i++) {
            options.push({ value: listado_eventos[i].id, label: listado_eventos[i].tipo_evento?.nombre + ' (' + listado_eventos[i].paciente?.nombre + ' - ' + listado_eventos[i].espacio?.nombre + ') el ' + new Date(listado_eventos[i].fecha_ingreso).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' ' + new Date(listado_eventos[i].fecha_ingreso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) });
        }

        setEventosFilter(options);
    }, [listado_eventos]);

    useEffect(() => {
        setProductosTotales(listado_productos);
    }, [listado_productos]);

    useEffect(() => {
        let options = [];

        for (var i = 0; i < listado_servicios.length; i++) {
            options.push({ value: listado_servicios[i].id, label: listado_servicios[i].nombre, categoria: listado_servicios[i].categoria, subcategoria: listado_servicios[i].subcategoria, subsubcategoria: listado_servicios[i].subsubcategoria, precio: listado_servicios[i].precio_venta });
        }

        setServiciosFilter(options);
    }, [listado_servicios]);

    useEffect(() => {
        let options = [];

        for (var i = 0; i < listado_paquetes.length; i++) {
            options.push({ value: listado_paquetes[i].id, label: listado_paquetes[i].nombre, productos: listado_paquetes[i].productos, servicios: listado_paquetes[i].servicios });
        }

        setPaquetesFilter(options);
    }, [listado_paquetes]);

    useEffect(() => {
        dispatch(editarBreadcrumb(rutasBread));
    }, [rutasBread]);    

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .select-group {
                            height: 100%;
                        }

                        .button-append {
                            padding: 0.39rem 0.70rem !important;
                            border-radius: 0 25% 25% 0 !important;
                            height: 100%;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <FiltrosProducto />

            <div className="row">
                <div className="col-md-12 px-4 pb-4">
                    <h3 className="mb-5 mt-5">Nueva comanda</h3>

                    <div className="row mb-3">
                        <div className="col-md-5 form-group mb-2">
                            <div className="input-group h-100">
                                <Select
                                    name="id_evento"
                                    className="select-group"
                                    classNamePrefix="react-select"
                                    placeholder="Evento a surtir"
                                    options={eventosFilter}
                                    value={eventoData}
                                    isClearable={true}
                                    onChange={(value) => handleSelectEvento(value)}
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    components={{
                                        ValueContainer: CustomValueContainer
                                    }}
                                    styles={{ ...styles_select }}
                                />
                            </div>
                        </div>

                        <div className="col-md-2 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input
                                        id="monto_calculado"
                                        name="monto_calculado"
                                        type="text" 
                                        className="form-control text-center"
                                        value={formatNumber(comandaData.monto_calculado, 2, '', '')}
                                        disabled
                                    />
                                    <label htmlFor="monto_calculado">Monto calculado</label>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-5 mt-4">
                            <button className="btn btn-success float-end" onClick={handleGuardarComanda}>Guardar</button>
                            <Link to='/comandas'><button className="btn btn-danger float-end me-2">Cancelar</button></Link>
                        </div>
                    </div>

                    <ul className="nav nav-pills nav-fill">
                        <li className="nav-item">
                            <div className="input-group-append">
                                <button type="button" className={'nav-link border border-primary' + (tipoComanda == 0 ? ' text-black bg-primary' : '')} onClick={() => setTipoComanda(0)}>Productos ({comandaData.productos.length})</button>
                            </div>
                        </li>
                        <li className="nav-item">
                            <div className="input-group-append">
                                <button type="button" className={'nav-link border border-primary' + (tipoComanda == 1 ? ' text-black bg-primary' : '')} onClick={() => setTipoComanda(1)}>Servicios ({comandaData.servicios.length})</button>
                            </div>
                        </li>
                        <li className="nav-item">
                            <div className="input-group-append">
                                <button type="button" className={'nav-link border border-primary' + (tipoComanda == 2 ? ' text-black bg-primary' : '')} onClick={() => setTipoComanda(2)}>Paquetes</button>
                            </div>
                        </li>
                    </ul>
                    
                    {
                        tipoComanda == 0 &&
                            <div className="productos row mt-4">
                                <div className="row mb-3">
                                    <h5>Productos a solicitar</h5>

                                    <DataTable 
                                        columns={columnsAdd}
                                        data={productosSolicitar}
                                        highlightOnHover={true}
                                    />
                                </div>

                                <hr className="mt-5" />

                                <div className="row mb-3">
                                    <h5>Productos totales</h5>

                                    <div className="d-flex justify-content-end mb-3">
                                        <button type="button" className="btn btn-secondary mx-1" onClick={() => handleFiltrosBusqueda()}>Filtros de búsqueda</button>
                                    </div>

                                    <DataTable 
                                        columns={columnsAll}
                                        data={productosTotales}
                                        pagination
                                        paginationPerPage={20}
                                        paginationRowsPerPageOptions={[5, 10, 20, 50]}
                                        paginationComponentOptions={paginationOptions}
                                        highlightOnHover={true}
                                    />
                                </div>
                            </div>
                    }

                    {
                        tipoComanda == 1 &&
                            <div className="servicios row mt-4">
                                <div className="col-md-12 form-group mb-3">
                                    <div className="input-group">
                                        {
                                            buscadorServicios &&
                                                <div className="form-floating h-100">
                                                    <Select
                                                        name="id_servicio"
                                                        className="select-group"
                                                        classNamePrefix="react-select"
                                                        placeholder="Servicios"
                                                        options={serviciosFilter}
                                                        value={servicioData}
                                                        isClearable={true}
                                                        onChange={(value) => handleSelectServicio(value)}
                                                        menuPortalTarget={document.body}
                                                        menuPlacement="auto"
                                                        components={{
                                                            ValueContainer: CustomValueContainer
                                                        }}
                                                        styles={{ ...styles_select }}
                                                    />
                                                </div>
                                        }

                                        {
                                            !buscadorServicios &&
                                                <div className="form-floating">
                                                    <input id="busqueda_servicio" name="busqueda_servicio" type="text" className="form-control text-center" onChange={e => handleInputChange(e)} />
                                                    <label htmlFor="busqueda_servicio">Buscador de servicios</label>
                                                </div>
                                        }

                                        <div className="input-group-append">
                                            <button className={'btn button-append btn-' + (buscadorServicios ? 'secondary' : 'primary')} onClick={() => handleBuscarServicios()}><i className={'fa-solid fa-' + (buscadorServicios ? 'xmark' : 'search')}></i></button>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-12 form-group mb-3">
                                    <DataTable
                                        columns={columnsServicios}
                                        data={comandaData.servicios}
                                        highlightOnHover={true}
                                    />
                                </div>
                            </div>
                    }

                    {
                        tipoComanda == 2 &&
                            <div className="paquetes row mt-4">
                                <div className="col-md-12 form-group mb-3">
                                    <div className="input-group">
                                        {
                                            buscadorPaquetes &&
                                                <div className="form-floating h-100">
                                                    <Select
                                                        name="id_paquete"
                                                        className="select-group"
                                                        classNamePrefix="react-select"
                                                        placeholder="Paquetes"
                                                        options={paquetesFilter}
                                                        value={paqueteData}
                                                        isClearable={true}
                                                        onChange={(value) => handleSelectPaquete(value)}
                                                        menuPortalTarget={document.body}
                                                        menuPlacement="auto"
                                                        components={{
                                                            ValueContainer: CustomValueContainer
                                                        }}
                                                        styles={{ ...styles_select }}
                                                    />
                                                </div>
                                        }

                                        {
                                            !buscadorPaquetes &&
                                                <div className="form-floating">
                                                    <input id="busqueda_paquete" name="busqueda_paquete" type="text" className="form-control text-center" onChange={e => handleInputChange(e)} />
                                                    <label htmlFor="busqueda_paquete">Buscador de paquetes</label>
                                                </div>
                                        }

                                        <div className="input-group-append">
                                            <button className={'btn button-append btn-' + (buscadorPaquetes ? 'secondary' : 'primary')} onClick={() => handleBuscarPaquetes()}><i className={'fa-solid fa-' + (buscadorPaquetes ? 'xmark' : 'search')}></i></button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    }
                </div>
            </div>
        </Fragment>
    )
}

export default NuevaComanda;